import { Injectable } from "@angular/core";

type Theme = {
  "customizable-primary-color": string;
  "customizable-second-color": string;
  "customizable-third-color": string;
  "customizable-border-radius": string;
  "customizable-border-radius2": string;
  "customizable-text-primary-red-button": string;
  "customizable-error-color": string;
  "customizable-error-icon": string;
  "customizable-red-weak": string;
  "customizable-success-color": string;
  "customizable-primary-button-on-press": string;
  "customizable-red-button-on-press": string;
};

export type ThemeKey = "aau" | "uventex" | "owtma";

type ThemeConfig = {
  vars: Theme;
  // Body class that scopes the theme stylesheet (e.g. assets/styles/owtma-theme.scss)
  bodyClass?: string;
  // Body class toggled on routes whose page content uses the theme's dark styles
  darkPageClass?: string;
  // Paths (no query/hash) that get darkPageClass: exact strings or patterns
  darkRoutes?: (string | RegExp)[];
};

// To add a subdomain theme: register it here, add its stylesheet (assigning the --sh-* tokens
// from assets/styles/_theme-tokens.scss under bodyClass), and call setSubdomainTheme() in main.ts.
const THEMES: Record<ThemeKey, ThemeConfig> = {
  uventex: {
    vars: {
      "customizable-primary-color": "#01a1da",
      "customizable-second-color": "#0095CE",
      "customizable-third-color": "#def6ff",
      "customizable-border-radius": "8px",
      "customizable-border-radius2": "16px",
      "customizable-success-color": "#3e8f3e",
      "customizable-text-primary-red-button": "#E15F5F",
      "customizable-error-color": "#FFEDED",
      "customizable-error-icon": "#E15F5F",
      "customizable-red-weak": "#FFD7D7",
      "customizable-primary-button-on-press": "#008BC0",
      "customizable-red-button-on-press": "#FFCECE",
    },
  },
  aau: {
    vars: {
      "customizable-primary-color": "#0e2a8d",
      "customizable-second-color": "#0d6efd",
      "customizable-third-color": "#def6ff",
      "customizable-border-radius": "0px",
      "customizable-border-radius2": "0px",
      "customizable-text-primary-red-button": "white",
      "customizable-error-color": "#d10d14",
      "customizable-success-color": "#3e8f3e",
      "customizable-error-icon": "#FFFFFF",
      "customizable-red-weak": "#ff5457",
      "customizable-primary-button-on-press": "#1b1b1b",
      "customizable-red-button-on-press": "#1b1b1b",
    },
  },
  // `owt` subdomain. Base vars stay light-friendly: only designed pages are dark.
  owtma: {
    vars: {
      "customizable-primary-color": "#dfb565",
      "customizable-second-color": "#c9973a",
      "customizable-third-color": "#f7efdf",
      "customizable-border-radius": "8px",
      "customizable-border-radius2": "16px",
      "customizable-text-primary-red-button": "#E15F5F",
      "customizable-error-color": "#FFEDED",
      "customizable-success-color": "#3e8f3e",
      "customizable-error-icon": "#E15F5F",
      "customizable-red-weak": "#FFD7D7",
      "customizable-primary-button-on-press": "#b8862f",
      "customizable-red-button-on-press": "#FFCECE",
    },
    bodyClass: "owtma-theme",
    darkPageClass: "owt-dark-page",
    darkRoutes: [
      "/",
      "/events",
      "/hub-studios",
      "/rankings",
      // Event detail and its medals table
      /^\/events\/event\/\d+(\/medals)?$/,
      // Registration flow: /registration/:eventId(/new|/:personId|/subevent/:subEventId/...)
      /^\/registration\/\d+(\/.*)?$/,
      // Assisted registration: athletes reuse the registration flow, officials the official one
      /^\/assisted-registration\/\d+\/(athletes|officials)(\/.*)?$/,
      // Default event registration: /event/:eventId/registration(/new|/:personId)
      /^\/event\/\d+\/registration(\/.*)?$/,
      // Official, volunteer, referee and coach registration
      /^\/registration-(official|volunteer|referee|coach)\/\d+$/,
      // Buy tickets flow: /buy-tickets/:eventId
      /^\/buy-tickets\/\d+(\/.*)?$/,
      "/verification-insurance",
      // Athletes list and profile
      /^\/athletes(\/\d+)?$/,
      // Event athlete list opened from the dashboard schedule
      /^\/dashboard\/schedule\/\d+$/,
      // Dashboard flights and standings
      /^\/dashboard\/(flights|standings)\/\d+$/,
      // Areas management (and its dialogs, which inherit the body tokens)
      /^\/dashboard\/event\/\d+\/management\/areas$/,
      // Staff credentials and staff management (their dialogs inherit the body tokens)
      /^\/dashboard\/event\/\d+\/management\/(staff-credentials|staff)$/,
      "/bulk-verify",
      // Memberships landing page (also served as /applications and /memberships/aau)
      "/memberships",
      "/applications",
      "/memberships/aau",
      // Membership flow: every step (/membership/:eventId/...)
      /^\/membership(\/.*)?$/,
      // Public event pages: studio detail, TNT views and public reports
      /^\/hub-studios\/studio\/[^/]+(\/diff)?$/,
      /^\/tnt\/(schedule|standings|competitors)\/\d+$/,
      /^\/public\/(schedule|competitors-report|standings-report)\/\d+$/,
      // Store
      /^\/store\/(list|cart|product\/[^/]+\/[^/]+)$/,
      // Create event
      "/create-event",
      "/create-new-event",
      /^\/create-event\/new-event\/\d+$/,
      // Other standalone pages
      "/leagues",
      "/meets",
      "/judge-assignment",
      "/aau-lookup",
      "/aau-reads",
      "/pm/view",
      "/actions/membership/auto-renew/opt-out",
      /^\/award-ceremony\/\d+\/presenter$/,
      /^\/compliance\/policy-exception\/[^/]+$/,
    ],
  },
};

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private keyTheme: ThemeKey = "uventex";
  // Theme the app falls back to; a subdomain theme replaces Uventex here
  private defaultTheme: ThemeKey = "uventex";
  private currentPath = "/";

  constructor() {
    this.applyUventexTheme();
  }

  private setTheme(key: ThemeKey) {
    const config = THEMES[key];
    Object.entries(config.vars).forEach(([name, value]) => {
      document.documentElement.style.setProperty(`--${name}`, value);
    });

    Object.entries(THEMES).forEach(([themeKey, themeConfig]) => {
      if (themeConfig.bodyClass) {
        document.body.classList.toggle(themeConfig.bodyClass, themeKey === key);
      }
    });

    this.keyTheme = key;
    this.updateForRoute(this.currentPath);
  }

  getTheme() {
    return this.keyTheme;
  }

  // Makes `key` the default theme (used for subdomains) and applies it
  setSubdomainTheme(key: ThemeKey) {
    this.defaultTheme = key;
    this.setTheme(key);
  }

  // Undoes setSubdomainTheme: back to the standard look (admin theme toggle)
  clearSubdomainTheme() {
    this.defaultTheme = "uventex";
    this.setTheme("uventex");
  }

  // Call on every navigation: toggles the active theme's dark-page class
  updateForRoute(path: string) {
    this.currentPath = path.split(/[?#]/)[0] || "/";

    Object.entries(THEMES).forEach(([themeKey, config]) => {
      if (!config.darkPageClass) {
        return;
      }
      const isDark =
        themeKey === this.keyTheme &&
        !!config.darkRoutes?.some((route) =>
          typeof route === "string" ? route === this.currentPath : route.test(this.currentPath)
        );
      document.body.classList.toggle(config.darkPageClass, isDark);
    });
  }

  // Resets to the default theme; on a themed subdomain that's the subdomain theme
  applyUventexTheme() {
    this.setTheme(this.defaultTheme);
  }

  // Pages switch to AAU styling for AAU events; a subdomain theme takes precedence
  applyAAUTheme() {
    this.setTheme(this.defaultTheme === "uventex" ? "aau" : this.defaultTheme);
  }
}
